import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';
import { getQualificationBadgeVariant, getQualificationBadgeClassName, getQualificationBadgeLabel } from '@/lib/badgeUtils';
import { DataSurface } from '@/components/platform/DataSurface';
import { TooltipLabel } from '@/components/ui/TooltipLabel';

export interface DemoTableRow {
  id: string;
  email: string;
  qualification_status?: string;
  booked_status?: string;
  role?: string;
  date: string;
  duration?: number; // in seconds
}

interface SessionsTableProps {
  sessions: DemoTableRow[];
  isLoading?: boolean;
  className?: string;
  pageSize?: number;
}

const formatDuration = (seconds?: number) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (!minutes) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`;
};

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInitials = (email: string) => {
  const name = email.split('@')[0];
  if (!name) return '?';
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const TableSkeleton = () => (
  <>
    {[1, 2, 3, 4, 5].map((i) => (
      <TableRow key={i}>
        <TableCell>
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        </TableCell>
        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
      </TableRow>
    ))}
  </>
);

export const SessionsTable: React.FC<SessionsTableProps> = ({
  sessions,
  isLoading = false,
  className,
  pageSize = 10,
}) => {
  const navigate = useNavigate();
  const pagination = usePagination({ totalItems: sessions.length, itemsPerPage: pageSize });
  const paginatedSessions = pagination.currentItems(sessions);

  const handleRowClick = (demo: DemoTableRow) => {
    navigate(`/analytics/sessions?sessionId=${encodeURIComponent(demo.id)}`);
  };

  return (
    <DataSurface className={className}>
      <div className="border-b border-border/80 px-4 py-3">
        <TooltipLabel
          label="Recent sessions"
          description="Latest sessions and outcomes."
          className="text-sm font-medium text-foreground"
        />
      </div>
      <div>
        <div className="overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="border-b border-border/70 hover:bg-transparent">
                <TableHead className="text-[11px] font-medium uppercase text-muted-foreground">Person</TableHead>
                <TableHead className="text-[11px] font-medium uppercase text-muted-foreground">Status</TableHead>
                <TableHead className="text-[11px] font-medium uppercase text-muted-foreground">Role</TableHead>
                <TableHead className="text-[11px] font-medium uppercase text-muted-foreground">Date</TableHead>
                <TableHead className="text-[11px] font-medium uppercase text-muted-foreground">Length</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableSkeleton />
              ) : paginatedSessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-subtle">
                    No sessions found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSessions.map((demo) => (
                  <TableRow
                    key={demo.id}
                    onClick={() => handleRowClick(demo)}
                    className="cursor-pointer border-b border-border/70 transition-colors last:border-0 hover:bg-accent/50"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="card-secondary-bg text-muted-foreground text-xs font-medium">
                            {getInitials(demo.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-heading truncate max-w-[200px]">
                          {demo.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getQualificationBadgeVariant(demo.qualification_status, demo.booked_status)}
                        className={cn('capitalize', getQualificationBadgeClassName(demo.qualification_status, demo.booked_status))}
                      >
                        {getQualificationBadgeLabel(demo.qualification_status, demo.booked_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {demo.role || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(demo.date)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDuration(demo.duration)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {sessions.length > pageSize && (
          <div className="flex items-center justify-between border-t border-border/80 px-4 py-3">
            <div className="text-sm text-muted-foreground">
              Showing {pagination.startIndex + 1}–{Math.min(pagination.endIndex, sessions.length)} of {sessions.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.goToPreviousPage}
                disabled={!pagination.canGoPrevious}
                className="h-8 w-8 p-0 border-default hover:bg-accent/60"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pagination.getPageNumbers().map((page) => (
                <Button
                  key={page}
                  variant={page === pagination.currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => pagination.setCurrentPage(page)}
                  className={cn(
                    "h-8 w-8 p-0",
                    page !== pagination.currentPage && "border-default hover:bg-accent/60"
                  )}
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.goToNextPage}
                disabled={!pagination.canGoNext}
                className="h-8 w-8 p-0 border-default hover:bg-accent/60"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DataSurface>
  );
};
